import { useEffect } from 'react'

// Port of assets/atz.js — count-up stats + journey arrow reveal.
// Re-runs whenever `key` (the route path) changes so freshly mounted
// sections get wired up.
export function useAtzScripts(key: string) {
  useEffect(() => {
    const observers: IntersectionObserver[] = []

    // ── count-up stats ──
    const parseNum = (raw: string) => {
      const m = raw.match(/^(\D*?)([\d.,]+)(.*)$/s)
      if (!m) return null
      const numStr = m[2]
      const decimals = numStr.indexOf('.') > -1 ? numStr.split('.')[1].length : 0
      return {
        prefix: m[1] || '',
        target: parseFloat(numStr.replace(/,/g, '')),
        hasComma: numStr.indexOf(',') > -1,
        decimals,
      }
    }
    type Info = ReturnType<typeof parseNum>
    const fmt = (v: number, info: NonNullable<Info>) => {
      let s = v.toFixed(info.decimals)
      if (info.hasComma) {
        const parts = s.split('.')
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')
        s = parts.join('.')
      }
      return info.prefix + s
    }
    const animate = (node: Text, info: NonNullable<Info>) => {
      const dur = 1400
      let start: number | null = null
      const stepFn = (ts: number) => {
        if (start === null) start = ts
        const p = Math.min((ts - start) / dur, 1)
        const eased = 1 - Math.pow(1 - p, 3)
        node.textContent = fmt(info.target * eased, info)
        if (p < 1) requestAnimationFrame(stepFn)
        else node.textContent = fmt(info.target, info)
      }
      requestAnimationFrame(stepFn)
    }

    const reduce =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches

    const nums = Array.from(
      document.querySelectorAll<HTMLElement>('.stat .n, [data-countup]')
    )
    const targets: HTMLElement[] = []
    nums.forEach((el) => {
      const lead = el.childNodes[0]
      if (!lead || lead.nodeType !== 3) return
      const info = parseNum((lead.textContent || '').trim())
      if (!info || !isFinite(info.target) || info.target === 0) return
      ;(el as any)._leadNode = lead
      ;(el as any)._countInfo = info
      targets.push(el)
    })
    if (targets.length && !reduce && 'IntersectionObserver' in window) {
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) {
              const el = e.target as HTMLElement
              io.unobserve(el)
              animate((el as any)._leadNode, (el as any)._countInfo)
            }
          })
        },
        { threshold: 0.4 }
      )
      targets.forEach((el) => io.observe(el))
      observers.push(io)
    }

    // ── journey arrow reveal ──
    const journeys = Array.from(document.querySelectorAll<HTMLElement>('.journey'))
    if (journeys.length && !reduce) {
      document.documentElement.classList.add('js-anim')
      const reveal = (el: Element) => el.classList.add('in-view')
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver(
          (entries) => {
            entries.forEach((e) => {
              if (e.isIntersecting) {
                reveal(e.target)
                io.unobserve(e.target)
              }
            })
          },
          { threshold: 0, rootMargin: '0px 0px -15% 0px' }
        )
        journeys.forEach((el) => io.observe(el))
        observers.push(io)
      } else {
        journeys.forEach(reveal)
      }
    }

    return () => observers.forEach((io) => io.disconnect())
  }, [key])
}
